// ============================================================================
// ONE answer to "which picture belongs to this model" — the whole system asks here
// ============================================================================
// Before this file every screen looked for a model's picture in its own place (Orders Models and the Morning
// Briefing in the Sales master only, the Table export in the library, the dev card and the sample sheet), so a
// new model that was not sold yet showed "No photo" on one screen and a photo on the next.
// The order is fixed, the same for every screen (David, 28.10):
//   1. the photo library (files_links ⨝ files_assets, kind model_photo) of the item code, then of its dev no —
//      lowest colour number first, then an unnumbered photo, then an option photo   (files-bank path)
//   2. the Sales master `image_small` (one row per colour: pickPhotos decides which)  (public url)
//   3. the development card's primary image, by dev no                                (public url)
//   4. the first sample-sheet colourway photo of the dev no                            (files-bank path)
// Every reader below is the one the Collection Book already uses (book/data/bookSourcesApi). A source that
// fails to read is reported in `failed`, never silently treated as "no photo".
// A screen reads pictures through src/hooks/useModelPhotos.js and draws them with components/system/ModelPhotoImg.js
// (a files-bank path is drawn as a thumbnail, never as the full original); the Table export calls resolveModelPhotoSources
// directly. Do not read a model picture from a table anywhere else.
// ============================================================================
import { supabase } from '../supabase';
import { tokenOf, isCL } from '../portals/book/model/bookKeys';
import { fetchOtherPhotos, fetchDevPhotos, fetchSampleColourways } from '../portals/book/data/bookSourcesApi';
import { pickPhotos, photoCodes, readPhotoRows } from './modelPhotoPick';

const EMPTY_LIB = { other: {}, option: {}, nocol: {} };
const firstOf = (o) => { const ks = Object.keys(o || {}).sort((a, b) => Number(a) - Number(b)); return ks.length ? o[ks[0]] : null; };

/**
 * The best library photo of one key, in this order: a numbered colour; an unnumbered photo that was read and is not an
 * option; an unnumbered one not yet read; a numbered option photo; an option photo with no colour. → { path } | null
 */
export function libraryPick(lib, key) {
  if (!lib || !key) return null;
  const a = firstOf(lib.other && lib.other[key]);
  if (a && a.p) return { path: a.p };
  const list = ((lib.nocol && lib.nocol[key]) || []).filter((x) => x && x.p);
  const plain = list.find((x) => !x.unread && !x.option) || list.find((x) => !x.option);
  if (plain) return { path: plain.p };
  const o = firstOf(lib.option && lib.option[key]);
  const o0 = o && o[0];
  if (o0 && o0.p) return { path: o0.p };
  return list.length ? { path: list[0].p } : null;
}

/**
 * EVERY usable source for ONE row, best first, from what the four readers returned:
 * [{ path, source } (a files-bank path) | { url, source, barcode? }]. A screen shows the first that loads.
 */
export function pickSources(item, dev, got) {
  const out = [];
  const ik = tokenOf(item); const dk = tokenOf(dev);
  for (const k of [ik, dk]) { const hit = k && libraryPick(got.lib, k); if (hit && !out.some((o) => o.path === hit.path)) out.push({ ...hit, source: 'library' }); }
  const s = got.sales && got.sales.get(String(item || '').trim());
  if (s && s.src) out.push({ url: s.src, barcode: s.barcode || null, source: 'sales' });
  if (dk && got.dev && got.dev.dev && got.dev.dev[dk]) out.push({ url: got.dev.dev[dk], source: 'dev' });
  const sm = dk && got.sample && got.sample[dk] && got.sample[dk].find((x) => x.p);
  if (sm) out.push({ path: sm.p, source: 'sample' });
  return out;
}

/** The best source of ONE row (the first of pickSources) or null. */
export const pickSource = (item, dev, got) => pickSources(item, dev, got)[0] || null;

const fetchSalesRows = async (codes) => {
  const { data, error } = await supabase.from('sales_data_master').select('model,barcode,image_small').in('model', codes).limit(1000);
  if (error) throw error;
  return data || [];
};

/**
 * pairs [{ item, dev? }] → { sources: [best source | null], candidates: [[every source, best first]], failed: { library?, sales?, dev?, sample? } }, aligned to pairs.
 * The four reads run together; one that throws is marked in `failed` and the others still answer.
 */
export async function resolveModelPhotoSources(pairs) {
  const rows = (pairs || []).map((p) => ({ item: p && p.item, dev: p && p.dev }));
  const items = rows.map((r) => tokenOf(r.item));
  const devs = rows.map((r) => tokenOf(r.dev));
  const keys = [...new Set([...items, ...devs].filter((k) => k && k !== 'OG'))];
  const devList = [...new Set(devs.filter((d) => d && d !== 'OG' && !isCL(d)))];
  const salesCodes = photoCodes(rows.map((r) => (r.item == null ? '' : String(r.item).trim())));
  const failed = {};
  const safe = async (name, fn, empty) => { try { return await fn(); } catch (e) { failed[name] = true; return empty; } };
  const [lib, sales, dev, sample] = await Promise.all([
    keys.length ? safe('library', () => fetchOtherPhotos(keys), EMPTY_LIB) : EMPTY_LIB,
    salesCodes.length ? safe('sales', async () => pickPhotos((await readPhotoRows(salesCodes, fetchSalesRows)).rows), new Map()) : new Map(),
    devList.length ? safe('dev', () => fetchDevPhotos(devList), { dev: {} }) : { dev: {} },
    devList.length ? safe('sample', () => fetchSampleColourways(devList), {}) : {},
  ]);
  const got = { lib, sales, dev, sample };
  const candidates = rows.map((r) => pickSources(r.item, r.dev, got));
  return { sources: candidates.map((c) => c[0] || null), candidates, failed };
}
